import { Fragment, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Search, Cpu, Image as ImageIcon, Film, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { GenerationsPanel } from '../components/projects/GenerationsPanel';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 25;

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpanded = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Model-usage diagnostic state
  const [muOpen, setMuOpen] = useState(false);
  const [muLoading, setMuLoading] = useState(false);
  const [muData, setMuData] = useState(null);
  const [muProjectId, setMuProjectId] = useState('');
  const [lookupId, setLookupId] = useState('');

  // Media viewer state
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaData, setMediaData] = useState(null);
  const [mediaProject, setMediaProject] = useState(null);

  const openModelUsage = async (projectId) => {
    const pid = (projectId || '').trim();
    if (!pid) {
      toast.error('Enter a project ID');
      return;
    }
    setMuProjectId(pid);
    setMuOpen(true);
    setMuLoading(true);
    setMuData(null);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/ai-management/projects/${encodeURIComponent(pid)}/model-usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMuData(res.data);
    } catch (error) {
      toast.error('Failed to fetch model usage');
    } finally {
      setMuLoading(false);
    }
  };

  const openMedia = async (project) => {
    setMediaProject(project);
    setMediaOpen(true);
    setMediaLoading(true);
    setMediaData(null);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/projects/${encodeURIComponent(project.id)}/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMediaData(res.data);
    } catch (error) {
      toast.error('Failed to fetch project media');
    } finally {
      setMediaLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const filteredProjects = useMemo(() => {
    let filtered = projects;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          (p.project_name || '').toLowerCase().includes(q) ||
          (p.user_email || '').toLowerCase().includes(q) ||
          (p.user_name || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((p) => p.status === statusFilter);
    }
    if (typeFilter !== 'all') {
      filtered = filtered.filter((p) => p.project_type === typeFilter);
    }
    return filtered;
  }, [searchTerm, statusFilter, typeFilter, projects]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / PAGE_SIZE));
  const pageProjects = filteredProjects.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'in-progress':
        return 'bg-blue-100 text-blue-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'video':
        return 'bg-purple-100 text-purple-700';
      case 'image':
        return 'bg-orange-100 text-orange-700';
      case 'audio':
        return 'bg-teal-100 text-teal-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading projects...</div>;
  }

  return (
    <div data-testid="projects-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Projects Management</h1>
        <p className="text-slate-600">One row per project — expand a row to see each of its generations</p>
      </div>

      {/* Model Usage lookup */}
      <div className="bg-white rounded-xl shadow-md p-5 border border-slate-200 mb-6" data-testid="model-usage-lookup">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-slate-700 font-semibold">
            <Cpu className="w-5 h-5 text-indigo-600" />
            Check model used for a Project
          </div>
          <div className="relative flex-1 min-w-72">
            <Input
              data-testid="model-usage-project-input"
              type="text"
              placeholder="Paste a Project ID (e.g. 4f27e6eb-2346-451b-9772-cc0ac4f5f282)"
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') openModelUsage(lookupId); }}
            />
          </div>
          <Button
            data-testid="model-usage-lookup-button"
            onClick={() => openModelUsage(lookupId)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            View Model Usage
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <div className="relative flex-1 min-w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              data-testid="project-search-input"
              type="text"
              placeholder="Search by project, user name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="relative z-20">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter" className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="z-50">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative z-20">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="type-filter" className="w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="z-50">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="audio">Audio</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {filteredProjects.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500">No projects found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="projects-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="w-10" />
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">User Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Project Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">User Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Created Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Last Activity</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Media</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Models</th>
                  </tr>
                </thead>
                <tbody>
                  {pageProjects.map((project) => (
                    <Fragment key={project.id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`project-row-${project.id}`}>
                      <td className="py-3 pl-3">
                        <button
                          type="button"
                          data-testid={`expand-project-${project.id}`}
                          aria-label="Toggle generations"
                          onClick={() => toggleExpanded(project.id)}
                          className="p-1 rounded hover:bg-slate-200 transition-colors"
                        >
                          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${expanded.has(project.id) ? '' : '-rotate-90'}`} />
                        </button>
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-slate-800">{project.user_name || '—'}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">
                        <div className="flex items-center gap-2">
                          <span>{project.project_name}</span>
                          <Badge
                            variant="outline"
                            className="text-xs text-slate-500 font-normal"
                            data-testid={`generation-count-${project.id}`}
                          >
                            {project.generation_count} {project.generation_count === 1 ? 'generation' : 'generations'}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{project.user_email}</td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getTypeColor(project.project_type)}`}>
                          {project.project_type}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                          {project.status === 'in-progress' ? 'In Progress' : project.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700">
                        {project.created_at ? format(new Date(project.created_at), 'MMM dd, yyyy') : '—'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700" data-testid={`last-activity-${project.id}`}>
                        {project.last_activity ? format(new Date(project.last_activity), 'MMM dd, yyyy HH:mm') : '—'}
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`view-media-${project.id}`}
                          disabled={!project.media_count}
                          onClick={() => openMedia(project)}
                        >
                          {project.project_type === 'video'
                            ? <Film className="w-4 h-4 mr-1" />
                            : <ImageIcon className="w-4 h-4 mr-1" />}
                          {project.media_count ? `View (${project.media_count})` : 'None'}
                        </Button>
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`view-models-${project.id}`}
                          onClick={() => openModelUsage(project.id)}
                        >
                          <Cpu className="w-4 h-4 mr-1" /> Models
                        </Button>
                      </td>
                    </tr>
                    {expanded.has(project.id) && (
                      <tr>
                        <td colSpan={10} className="p-0">
                          <GenerationsPanel project={project} onMedia={openMedia} onModels={openModelUsage} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-6 flex-wrap gap-3" data-testid="projects-pagination">
              <p className="text-sm text-slate-500">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredProjects.length)} of {filteredProjects.length} projects
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="pagination-prev"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="w-4 h-4" /> Prev
                </Button>
                <span className="text-sm text-slate-600 px-2" data-testid="pagination-info">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="pagination-next"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Media viewer dialog */}
      <Dialog open={mediaOpen} onOpenChange={setMediaOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="project-media-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-indigo-600" /> Completed Media
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2 mb-2">
            {mediaProject?.project_name}
            {mediaProject?.id?.startsWith('grp_')
              ? ' · all generations'
              : <> · <span className="break-all">{mediaProject?.id}</span></>}
          </p>

          {mediaLoading ? (
            <div className="py-10 text-center text-slate-500">Loading media…</div>
          ) : !mediaData ? (
            <div className="py-10 text-center text-slate-500">No data</div>
          ) : ((mediaData.images?.length || 0) === 0 && (mediaData.videos?.length || 0) === 0 && (mediaData.audio?.length || 0) === 0) ? (
            <div className="py-10 text-center text-slate-500">No completed media found for this project.</div>
          ) : (
            <div className="space-y-6">
              {(mediaData.videos?.length || 0) > 0 && (
                <div data-testid="media-videos">
                  <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                    Final Rendered Videos ({mediaData.videos.length})
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {mediaData.videos.map((v, i) => (
                      <video key={i} src={v.url} controls className="w-full rounded-lg border border-slate-200 bg-black" />
                    ))}
                  </div>
                </div>
              )}

              {(mediaData.images?.length || 0) > 0 && (
                <div data-testid="media-images">
                  <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                    Images ({mediaData.images.length})
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {mediaData.images.map((img, i) => (
                      <a key={i} href={img.url} target="_blank" rel="noreferrer" className="block group">
                        <img
                          src={img.url}
                          alt={img.filename}
                          loading="lazy"
                          className="w-full h-32 object-cover rounded-lg border border-slate-200 group-hover:ring-2 group-hover:ring-indigo-400 transition"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {(mediaData.audio?.length || 0) > 0 && (
                <div data-testid="media-audio">
                  <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                    Audio ({mediaData.audio.length})
                  </div>
                  <div className="space-y-2">
                    {mediaData.audio.map((a, i) => (
                      <audio key={i} src={a.url} controls className="w-full" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={muOpen} onOpenChange={setMuOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="model-usage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cpu className="w-5 h-5 text-indigo-600" /> Model Usage
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 break-all -mt-2 mb-2">
            {muProjectId.startsWith('grp_') ? 'All generations of this project' : `Project: ${muProjectId}`}
          </p>

          {muLoading ? (
            <div className="py-10 text-center text-slate-500">Loading model usage…</div>
          ) : !muData ? (
            <div className="py-10 text-center text-slate-500">No data</div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">Models used ({muData.total_steps} steps)</div>
                {muData.models_used_summary && muData.models_used_summary.length > 0 ? (
                  <div className="flex flex-wrap gap-2" data-testid="model-usage-summary">
                    {muData.models_used_summary.map((m) => (
                      <Badge key={m.model_name} className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100">
                        {m.model_name} · {m.count}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">
                    No generation jobs found for this project (records live in the environment where the project ran).
                  </p>
                )}
              </div>

              {muData.steps && muData.steps.length > 0 && (
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-sm" data-testid="model-usage-table">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-left">
                        <th className="py-2 px-3 font-semibold text-slate-600">Step</th>
                        <th className="py-2 px-3 font-semibold text-slate-600">Status</th>
                        <th className="py-2 px-3 font-semibold text-slate-600">Requested</th>
                        <th className="py-2 px-3 font-semibold text-slate-600">Model Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {muData.steps.map((s, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-2 px-3 text-slate-700">{s.step}</td>
                          <td className="py-2 px-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(s.status)}`}>{s.status || '—'}</span>
                          </td>
                          <td className="py-2 px-3 text-slate-600">{s.requested_model || '—'}</td>
                          <td className="py-2 px-3 text-slate-800 font-medium">
                            {s.model_used
                              ? (s.model_used.model_name || s.model_used.endpoint || s.model_used.model_id)
                              : <span className="text-slate-400 font-normal">not recorded</span>}
                            {s.model_used && s.model_used.provider && (
                              <span className="text-slate-400 font-normal"> ({s.model_used.provider})</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
